import * as THREE from 'three';
import { BLACK, basic, cloth, emis, metal, std } from '../materials.ts';
import { G, M, Ribbon, V, glowSprite, limb } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Cantor de huesos (docs/CRIATURAS.md §2): the era II summoner that raises the fallen as skeletons. A gaunt, hunched
 * thing under a heavy peaked hood, its long jaw hanging open in an endless dirge (a cold light shows in its throat);
 * the robe is torn open over the chest and a ribcage shows through it with the same bone-white light caged inside.
 * Long fingerbones; a staff that is a spine, crowned with a bone hook from which an iron bell and strung bones hang;
 * a few bones turn slowly round it and a faint sigil is scorched into the ground at its feet. The `raise` clip throws
 * the head back, lifts staff and claw and flares the chest (`core` bone). Opaque shared cloth, bone and iron; one
 * self-lit material (the song light: core, throat, eyes, sigil) and one translucent one (the grave mist), plus glows.
 */

/** The song light, cold bone-white going violet. */
const LIGHT = '#d9ccff';
/** Places other code may want (model coordinates): the chest light at rest and the staff's bell. */
export const BONESINGER = { core: V(0, 1.17, 0.12), bell: V(-0.36, 1.98, 0.5) };

/** An open, ragged bell of cloth (optionally an arc), torn at the hem, its wall folded. */
const drape = (rTop: number, rBot: number, h: number, folds: number, tear: number, rng: () => number, theta?: [number, number], seg = 12): THREE.BufferGeometry => {
  const geo = theta ? new THREE.CylinderGeometry(rTop, rBot, h, seg, 3, true, theta[0], theta[1]) : new THREE.CylinderGeometry(rTop, rBot, h, seg, 3, true);
  const p = geo.attributes.position as THREE.BufferAttribute;
  const cuts = new Map<number, number>();
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
    const a = Math.atan2(x, z);
    const k = (h / 2 - y) / h;
    const f = 1 + 0.1 * k * Math.sin(a * folds + 0.4) + 0.04 * k * Math.sin(a * folds * 2.7);
    p.setX(i, x * f);
    p.setZ(i, z * f);
    if (k > 0.99) {
      const key = Math.round(a * 1000);
      let cut = cuts.get(key);
      if (cut === undefined) {
        cut = cuts.size % 2 ? -tear * (0.6 + rng() * 0.6) : tear * rng() * 0.4;
        cuts.set(key, cut);
      }
      p.setY(i, y + cut);
    }
  }
  geo.computeVertexNormals();
  return geo;
};

/** A long bone between a and b: a shaft with a knuckle at each end. */
const boneRod = (parent: THREE.Object3D, a: THREE.Vector3, b: THREE.Vector3, r: number, mat: THREE.Material): void => {
  limb(parent, a, b, r, r * 0.85, mat, 4);
  for (const e of [a, b]) {
    const k = M(new THREE.OctahedronGeometry(r * 1.8, 0), mat, parent);
    k.position.copy(e);
    k.scale.set(1, 0.8, 1);
  }
};

/**
 * Bones: body at the hips carries the robe (skirt) and the hunched chest; the chest carries the caged `core` light,
 * the head (with its hinged `jaw`), the staff arm (armL, foreL, the `staff` in the fist and the `bell` swinging from
 * its hook) and the free claw (armR, foreR). `orbit` (on the model group, so the body's lean doesn't tip it) carries the bones that turn round it. No legs: it
 * shuffles inside its robe. `orbit` hangs from the model group, not the body, so a fall can drop it flat.
 */
export const bonesinger = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 47);
  const { g, R, rng } = c;
  const robe = cloth('#211e25', { side: THREE.DoubleSide, bumpScale: 0.03 });
  const ash = cloth('#47434c', { side: THREE.DoubleSide, bumpScale: 0.035 });
  const bone = std('#cfcad3', { roughness: 0.7 });
  const old = std('#8d8693', { roughness: 0.85 });
  const iron = metal('#3a3940', { roughness: 0.5 });
  const cord = std('#2e2a31', { roughness: 0.9 });
  const song = emis('#efeaff', '#9d84ff', 1.9);

  const bodyJ = joint(g, V(0, 0.8, 0), 'body');
  const robeJ = joint(bodyJ.abs, V(0, 0.8, 0), 'robe');
  const chestJ = joint(bodyJ.abs, V(0, 0.92, -0.02), 'chest');
  const ch = chestJ.abs;
  const coreJ = joint(ch, BONESINGER.core, 'core');
  const headJ = joint(ch, V(0, 1.42, 0.13), 'head');
  const jawJ = joint(headJ.abs, V(0, 1.5, 0.26), 'jaw');
  const shL = V(-0.2, 1.36, 0.06), elL = V(-0.37, 1.1, 0.14), handL = V(-0.36, 1.2, 0.44);
  const shR = V(0.2, 1.36, 0.06), elR = V(0.4, 1.12, 0.16), handR = V(0.42, 1.3, 0.42);
  const armLJ = joint(ch, shL, 'armL');
  const foreLJ = joint(armLJ.abs, elL, 'foreL');
  const staffJ = joint(foreLJ.abs, handL, 'staff');
  const TOP = V(-0.36, 2.1, 0.46);
  const HOOK = V(-0.36, 2.06, 0.6);
  const bellJ = joint(staffJ.abs, HOOK, 'bell');
  const armRJ = joint(ch, shR, 'armR');
  const foreRJ = joint(armRJ.abs, elR, 'foreR');
  const orbitJ = joint(g, V(0, 1.0, 0), 'orbit');

  // The sigil scorched round its feet: a ring of the song light, broken, with a few runes and old bones on it.
  const sig = G(g, 0, 0.008, 0);
  for (let i = 0; i < 3; i++) {
    const arc = M(new THREE.RingGeometry(0.505, 0.52, 12, 1, i * 2.09 + 0.2, 1.6), song, sig);
    arc.rotation.x = -Math.PI / 2;
  }
  for (let i = 0; i < 6; i++) {
    const a = i * 1.047 + 0.5;
    const rn = M(new THREE.PlaneGeometry(0.014, 0.075), song, sig);
    rn.position.set(Math.cos(a) * 0.6, 0, Math.sin(a) * 0.6);
    rn.rotation.set(-Math.PI / 2, 0, a + (i % 2 ? 0.5 : -0.4));
  }
  for (let i = 0; i < 4; i++) {
    const a = rng() * 6.3, d = 0.45 + rng() * 0.15;
    const p = V(Math.cos(a) * d, 0.015, Math.sin(a) * d);
    const q = p.clone().add(V(R() * 0.18, 0, R() * 0.18));
    boneRod(g, p, q, 0.011, i % 2 ? old : bone);
  }
  const skullG = M(new THREE.SphereGeometry(0.05, 6, 5), old, g);
  skullG.position.set(0.36, 0.035, 0.32);
  skullG.scale.set(0.9, 0.8, 1.1);

  // The robe: a long, heavy ash-black bell from the waist, torn at the hem, and a shorter ragged overskirt of grey.
  const skirt = M(drape(0.17, 0.45, 0.86, 7, 0.13, rng), robe, robeJ.abs);
  skirt.position.set(0, 0.43, -0.01);
  const over = M(drape(0.2, 0.36, 0.48, 5, 0.17, rng, [Math.PI * 0.35, Math.PI * 1.3], 9), ash, robeJ.abs);
  over.position.set(0, 0.66, 0);
  // The belly of the robe under the bared ribs, so the cage sits on cloth and not on the skirt's open mouth.
  const belly = M(new THREE.CylinderGeometry(0.15, 0.17, 0.2, 8), robe, robeJ.abs);
  belly.position.set(0, 0.93, 0.0);
  // A rope belt with a bundle of fingerbones knotted on it.
  const belt = M(new THREE.TorusGeometry(0.17, 0.016, 3, 9), cord, bodyJ.abs);
  belt.rotation.x = Math.PI / 2 - 0.12;
  belt.position.set(0, 0.86, 0.02);
  for (let i = 0; i < 4; i++) limb(robeJ.abs, V(0.12 + i * 0.012, 0.84, 0.13), V(0.13 + i * 0.02 + R() * 0.02, 0.66 - i * 0.03, 0.17), 0.007, 0.006, bone, 3);

  // The hunched back: robe over a bowed spine, shoulders thrust forward; it covers the back and sides only.
  const back = M(new THREE.SphereGeometry(0.21, 10, 7, Math.PI * 0.84, Math.PI * 1.32), robe, ch);
  back.scale.set(1.02, 1.3, 0.9);
  back.position.set(0, 1.18, 0.0);
  back.rotation.x = 0.35;
  const hump = M(new THREE.SphereGeometry(0.17, 7, 5), robe, ch);
  hump.position.set(0, 1.33, -0.07);
  hump.scale.set(1.15, 0.85, 1);
  // A torn mantle of ash over the shoulders, hanging open in front.
  const mantle = M(drape(0.14, 0.38, 0.6, 5, 0.15, rng, [Math.PI * 0.3, Math.PI * 1.4], 11), ash, ch);
  mantle.position.set(0, 1.12, 0.0);
  mantle.rotation.x = 0.22;
  // Rags of the robe's front hanging off the torn edges.
  for (const k of [-1, 1]) {
    const rg = M(new THREE.PlaneGeometry(0.08, 0.34), robe, ch);
    rg.position.set(k * 0.17, 1.06, 0.15);
    rg.rotation.set(0.25, k * 0.55, k * 0.08);
  }

  // The ribcage, bared: a spine, a sternum and six ribs that curve round the light caged inside.
  for (let i = 0; i < 5; i++) {
    const y = 1.04 + i * 0.085;
    const v = M(new THREE.CylinderGeometry(0.026, 0.026, 0.05, 4, 1, true), old, ch);
    v.position.set(0, y, -0.05 + 0.08 * Math.sin((i / 4) * 1.6));
  }
  const stern = M(new THREE.BoxGeometry(0.035, 0.2, 0.02), bone, ch);
  stern.position.set(0, 1.25, 0.22);
  stern.rotation.x = -0.2;
  for (let i = 0; i < 5; i++) {
    const y = 1.38 - i * 0.075;
    const r = 0.14 + Math.sin((i / 4) * 2.2) * 0.035;
    const z0 = -0.03 + 0.02 * i;
    // From the spine at the back, round the flank, dropping toward the sternum (the low ones are broken short).
    const reach = i > 2 ? 0.55 : 1;
    for (const k of [-1, 1]) {
      const pts = [V(k * 0.02, y, z0 - 0.02), V(k * r * 0.95, y - 0.03, z0 + 0.06), V(k * r * (0.6 + 0.4 * (1 - reach)), y - 0.08, 0.17), V(k * (0.03 + (1 - reach) * 0.12), y - 0.12 * reach, 0.215 - (1 - reach) * 0.04)];
      M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 5, 0.015, 3), bone, ch);
    }
  }
  // Shoulder blades and collarbones.
  for (const k of [-1, 1]) {
    limb(ch, V(k * 0.03, 1.4, 0.18), V(k * 0.19, 1.38, 0.08), 0.012, 0.011, bone, 4);
  }

  // The caged light: a hard white core, a violet glow round it; `raise` blooms it.
  const cr = coreJ.abs;
  const C = BONESINGER.core;
  M(new THREE.IcosahedronGeometry(0.055, 0), song, cr).position.copy(C);
  const coreGlow = glowSprite(cr, '#a68cff', 0.75, C.clone().add(V(0, 0, 0.04)), 0.8, true);
  const coreHot = glowSprite(cr, '#f3eeff', 0.28, C.clone().add(V(0, 0, 0.05)), 0.95, true);
  // Threads of the light leaking out between the ribs.
  for (let i = 0; i < 4; i++) {
    const a = -1.2 + i * 0.8;
    const pts: THREE.Vector3[] = [];
    for (let j = 0; j < 5; j++) {
      const s = j / 4;
      pts.push(V(C.x + Math.sin(a) * (0.03 + 0.18 * s), C.y + 0.02 * Math.sin(i + s * 5) + (i % 2 ? 0.05 : -0.04) * s, C.z + Math.cos(a) * (0.02 + 0.16 * s)));
    }
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 5, 0.0035, 3), song, cr);
  }

  // The head: a long, gaunt skull of a face under a deep, heavy hood peaked forward.
  const hd = headJ.abs;
  const hg = new THREE.SphereGeometry(0.17, 10, 8, Math.PI / 2 + 0.9, Math.PI * 2 - 1.8, 0, Math.PI / 1.25);
  {
    // Drawn up to a heavy peak that leans forward over the brow, the opening's rim sagging.
    const hp = hg.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < hp.count; i++) {
      const y = hp.getY(i) / 0.17, z = hp.getZ(i) / 0.17;
      const up = Math.max(0, y - 0.35);
      hp.setY(i, hp.getY(i) + up * up * 0.16);
      hp.setZ(i, hp.getZ(i) + up * up * 0.2 + (z > 0.5 && y > -0.2 ? 0.015 : 0));
    }
    hg.computeVertexNormals();
  }
  const hood = M(hg, ash, hd);
  hood.position.set(0, 1.56, 0.2);
  hood.scale.set(1.05, 1.12, 1.12);
  hood.rotation.x = -0.25;
  M(new THREE.SphereGeometry(0.13, 6, 4), BLACK, hd).position.set(0, 1.55, 0.2);
  // The cowl falling from the hood down the back.
  const cowl = M(drape(0.13, 0.24, 0.32, 4, 0.08, rng, [Math.PI * 0.55, Math.PI * 0.9], 7), ash, hd);
  cowl.position.set(0, 1.44, 0.14);
  cowl.rotation.x = 0.2;
  // The skull: a narrow cranium, deep sockets, cheekbones and an upper jaw of long teeth.
  const skull = M(new THREE.SphereGeometry(0.085, 8, 6), bone, hd);
  skull.position.set(0, 1.585, 0.27);
  skull.scale.set(0.9, 1.05, 1.0);
  for (const k of [-1, 1]) {
    const so = M(new THREE.SphereGeometry(0.024, 5, 3), BLACK, hd);
    so.scale.set(1.2, 0.85, 0.45);
    so.position.set(k * 0.033, 1.585, 0.338);
    so.rotation.z = -k * 0.3;
    // A heavy brow ridge shading the sockets.
    const br = M(new THREE.BoxGeometry(0.05, 0.016, 0.03), bone, hd);
    br.position.set(k * 0.033, 1.613, 0.335);
    br.rotation.z = k * 0.3;
    M(new THREE.SphereGeometry(0.0065, 4, 3), song, hd).position.set(k * 0.033, 1.583, 0.346);
    glowSprite(hd, LIGHT, 0.06, V(k * 0.033, 1.583, 0.356), 0.85);
    const cheek = M(new THREE.SphereGeometry(0.022, 5, 3), bone, hd);
    cheek.position.set(k * 0.055, 1.545, 0.31);
    cheek.scale.set(1, 0.6, 1.3);
  }
  const maxilla = M(new THREE.CylinderGeometry(0.042, 0.034, 0.05, 6, 1, false, -Math.PI / 2, Math.PI), bone, hd);
  maxilla.position.set(0, 1.515, 0.3);
  M(new THREE.ConeGeometry(0.012, 0.025, 3), BLACK, hd).position.set(0, 1.553, 0.345);
  for (let i = 0; i < 6; i++) {
    const t = M(new THREE.ConeGeometry(0.007, 0.04, 3), bone, hd);
    t.position.set((i - 2.5) * 0.013, 1.48, 0.335 - Math.abs(i - 2.5) * 0.007);
    t.rotation.x = Math.PI;
  }
  // The throat's light: the song itself, seen through the open mouth.
  M(new THREE.OctahedronGeometry(0.03, 0), song, hd).position.set(0, 1.47, 0.28);
  const throat = glowSprite(hd, LIGHT, 0.24, V(0, 1.46, 0.33), 0.85, true);

  // The jaw: unnaturally long, unhinged and hanging open, a row of long teeth, a beard of cold mist off its point.
  const jw = jawJ.abs;
  const J1 = V(0, 1.23, 0.39);
  for (const k of [-1, 1]) {
    limb(jw, V(k * 0.05, 1.53, 0.27), V(k * 0.042, 1.36, 0.33), 0.012, 0.011, bone, 4);
    limb(jw, V(k * 0.042, 1.36, 0.33), V(k * 0.028, 1.24, 0.38), 0.011, 0.01, bone, 4);
  }
  const chin = M(new THREE.BoxGeometry(0.08, 0.035, 0.055), bone, jw);
  chin.position.copy(J1);
  chin.rotation.x = 0.4;
  for (let i = 0; i < 6; i++) {
    const t = M(new THREE.ConeGeometry(0.007, 0.05 + (i % 2) * 0.015, 3), bone, jw);
    const e = Math.abs(i - 2.5);
    t.position.set((i - 2.5) * 0.013, 1.27 + e * 0.02, 0.405 - e * 0.012);
    t.rotation.x = 0.35;
  }

  // The staff arm: the sleeve torn back to the elbow, a forearm of bare bone, fingers knotted round the staff.
  limb(armLJ.abs, shL, elL, 0.05, 0.042, robe, 6);
  const slL = M(drape(0.045, 0.1, 0.34, 4, 0.12, rng, undefined, 7), ash, armLJ.abs);
  slL.position.set(elL.x, elL.y - 0.02, elL.z);
  slL.rotation.set(0.3, 0, 0.2);
  boneRod(foreLJ.abs, elL, handL.clone().add(V(0.02, -0.03, -0.02)), 0.013, bone);
  limb(foreLJ.abs, elL.clone().add(V(0.02, 0, 0)), handL.clone().add(V(0.035, -0.03, -0.02)), 0.009, 0.009, old, 3);
  for (let f = 0; f < 3; f++) {
    const y = handL.y + 0.03 - f * 0.03;
    const k = M(new THREE.TorusGeometry(0.03, 0.008, 3, 5, Math.PI * 1.3), bone, foreLJ.abs);
    k.position.set(handL.x, y, handL.z);
    k.rotation.set(Math.PI / 2, 0, -0.6);
  }

  // The free claw: raised and spread toward the fallen, a bare forearm and five long jointed fingerbones.
  limb(armRJ.abs, shR, elR, 0.05, 0.042, robe, 6);
  const slR = M(drape(0.045, 0.1, 0.34, 4, 0.12, rng, undefined, 7), ash, armRJ.abs);
  slR.position.set(elR.x, elR.y - 0.02, elR.z);
  slR.rotation.set(0.3, 0, -0.2);
  boneRod(foreRJ.abs, elR, handR, 0.013, bone);
  limb(foreRJ.abs, elR.clone().add(V(-0.02, 0, 0)), handR.clone().add(V(-0.02, 0, 0)), 0.009, 0.009, old, 3);
  M(new THREE.BoxGeometry(0.07, 0.03, 0.07), bone, foreRJ.abs).position.copy(handR).add(V(0, 0.02, 0.02));
  for (let f = 0; f < 5; f++) {
    const sp = (f - 2) * 0.32;
    const th = f === 0 ? -0.6 : 0;
    const k0 = handR.clone().add(V(Math.sin(sp) * 0.04 + th * 0.04, 0.035, 0.04));
    const k1 = k0.clone().add(V(Math.sin(sp) * 0.075 + th * 0.06, 0.09 - Math.abs(sp) * 0.03, 0.06));
    const k2 = k1.clone().add(V(Math.sin(sp) * 0.05, 0.05, 0.075));
    const k3 = k2.clone().add(V(Math.sin(sp) * 0.03, -0.015, 0.07));
    limb(foreRJ.abs, k0, k1, 0.009, 0.008, bone, 3);
    limb(foreRJ.abs, k1, k2, 0.008, 0.007, bone, 3);
    const tip = M(new THREE.ConeGeometry(0.007, 0.075, 3), old, foreRJ.abs);
    tip.position.copy(k2).lerp(k3, 0.5);
    tip.quaternion.setFromUnitVectors(V(0, 1, 0), k3.clone().sub(k2).normalize());
  }

  // The staff: a spine, vertebra on vertebra from the ground to above the hood, spurred, crowned with a bone hook.
  const st = staffJ.abs;
  const BOT = V(-0.36, 0.02, 0.43);
  limb(st, BOT, TOP, 0.016, 0.014, old, 5);
  const n = 15;
  for (let i = 0; i < n; i++) {
    const p = BOT.clone().lerp(TOP, (i + 0.5) / n);
    const s = 0.7 + 0.45 * (i / n);
    const vb = M(new THREE.CylinderGeometry(0.03 * s, 0.025 * s, 0.1, 5, 1, true), i % 2 ? old : bone, st);
    vb.position.copy(p);
    if (i > 3) {
      // Each vertebra's spur and wings.
      const sp = M(new THREE.ConeGeometry(0.013, 0.07 * s, 3), bone, st);
      sp.position.copy(p).add(V(0, 0, -0.04 * s));
      sp.rotation.x = -2.0;
      if (i % 2 === 0) {
        const w = M(new THREE.BoxGeometry(0.11 * s, 0.014, 0.018), bone, st);
        w.position.copy(p).add(V(0, 0.015, -0.01));
      }
    }
  }
  // The crown: a jawless skull lashed at the top and a curled hook of bone reaching forward over it.
  const sk = M(new THREE.SphereGeometry(0.055, 6, 4), bone, st);
  sk.position.copy(TOP).add(V(0, 0.03, 0.02));
  sk.scale.set(0.9, 0.95, 1.1);
  for (const k of [-1, 1]) {
    const e = M(new THREE.SphereGeometry(0.014, 4, 3), BLACK, st);
    e.position.copy(TOP).add(V(k * 0.02, 0.035, 0.075));
  }
  const hookPts = [TOP.clone().add(V(0, 0.06, -0.02)), TOP.clone().add(V(0, 0.16, 0.04)), TOP.clone().add(V(0, 0.12, 0.14)), HOOK.clone().add(V(0, 0.03, 0))];
  M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(hookPts), 8, 0.012, 4), bone, st);
  const knot = M(new THREE.TorusGeometry(0.03, 0.009, 3, 7), cord, st);
  knot.position.copy(TOP).add(V(0, -0.05, 0));
  knot.rotation.x = Math.PI / 2;

  // The bell on its cord from the hook, cracked iron, and a fringe of strung fingerbones that clatter round it.
  const bl = bellJ.abs;
  const B = BONESINGER.bell;
  limb(bl, HOOK, V(B.x, B.y + 0.03, B.z + 0.1), 0.004, 0.004, cord, 3);
  const bell = M(new THREE.CylinderGeometry(0.025, 0.055, 0.09, 8, 1, true), iron, bl);
  bell.position.set(B.x, B.y - 0.02, B.z + 0.1);
  const lip = M(new THREE.TorusGeometry(0.055, 0.007, 3, 8), iron, bl);
  lip.position.set(B.x, B.y - 0.065, B.z + 0.1);
  lip.rotation.x = Math.PI / 2;
  M(new THREE.SphereGeometry(0.027, 6, 3, 0, Math.PI * 2, 0, Math.PI / 2), iron, bl).position.set(B.x, B.y + 0.023, B.z + 0.1);
  M(new THREE.SphereGeometry(0.014, 4, 3), bone, bl).position.set(B.x, B.y - 0.08, B.z + 0.1);
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + 0.4;
    const top = V(B.x + Math.cos(a) * 0.045, B.y + 0.05, B.z + 0.1 + Math.sin(a) * 0.045);
    const end = top.clone().add(V(Math.cos(a) * 0.02, -0.12 - (i % 2) * 0.05, Math.sin(a) * 0.02));
    limb(bl, HOOK.clone().add(V(0, 0.01, 0)), top, 0.003, 0.003, cord, 3);
    boneRod(bl, top.clone().add(V(0, -0.03, 0)), end, 0.007, i % 2 ? old : bone);
  }

  // The bones that turn round it, drawn up by the song.
  const ob = orbitJ.abs;
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 + 0.3;
    const r = 0.5 + (i % 2) * 0.07;
    const y = 1.0 + 0.07 * Math.sin(a * 2 + 0.5);
    const mid = V(Math.cos(a) * r, y, Math.sin(a) * r);
    const dir = V(-Math.sin(a), 0.4 * Math.cos(a * 3), Math.cos(a)).normalize().multiplyScalar(0.06 + (i % 3) * 0.015);
    if (i === 2) {
      const s = M(new THREE.SphereGeometry(0.04, 6, 4), bone, ob);
      s.position.copy(mid);
      s.scale.set(0.9, 0.85, 1.1);
    } else boneRod(ob, mid.clone().sub(dir), mid.clone().add(dir), 0.012, i % 2 ? old : bone);
    glowSprite(ob, '#8f76ff', 0.18, mid, 0.35);
  }

  // Grave mist: rising off the hem and curling from its throat when it sings.
  const ribs: Ribbon[] = [];
  const mist = basic('#3a3156', 0.42);
  for (let i = 0; i < 2; i++) {
    const a = i * 2.6 + 1.2;
    const pts: THREE.Vector3[] = [];
    for (let j = 0; j < 6; j++) pts.push(V(Math.sin(a) * (0.44 - j * 0.035) + Math.sin(j * 0.9 + i) * 0.05, 0.03 + j * 0.1, Math.cos(a) * (0.44 - j * 0.035)));
    ribs.push(new Ribbon(robeJ.abs, pts, 0.025, mist, rng, 0.045));
  }
  glowSprite(ch, '#7a5cff', 0.9, V(0, 1.2, 0.12), 0.18);

  // Only the boil and the flicker here: the swaying, the jaw and the flare are the clips'.
  const t = (): void => {
    for (const r of ribs) r.step();
    coreGlow.material.opacity = 0.65 + rng() * 0.25;
    coreHot.material.opacity = 0.8 + rng() * 0.2;
    throat.material.opacity = 0.6 + rng() * 0.3;
  };
  t();
  return done(c, 1.85, t, undefined, bonesOf(c, {
    body: bodyJ.p, robe: robeJ.p, chest: chestJ.p, core: coreJ.p, head: headJ.p, jaw: jawJ.p,
    armL: armLJ.p, foreL: foreLJ.p, staff: staffJ.p, bell: bellJ.p, armR: armRJ.p, foreR: foreRJ.p, orbit: orbitJ.p,
  }));
};
